/**
 * EBIOS RM Entity ID Generator
 * 
 * Provides consistent, readable, and analysis-scoped ID generation
 * for all entities across the application.
 * 
 * ID Format: {analysisShortId}-{entityPrefix}{paddedNumber}
 * Example: "A001-vm01" = Analysis A001, Valeur M\u00e9tier (Business Value) 01
 * 
 * @module utils/idGenerator
 */

/**
 * Entity type to prefix mapping
 * Using French abbreviations where appropriate for EBIOS RM context
 */
export const ENTITY_PREFIXES = {
    // Workshop 1 entities
    participant: 'p',           // Participant
    responsiblePerson: 'rp',    // Responsable
    businessValue: 'vm',        // Valeur M\u00e9tier
    supportAsset: 'bs',         // Bien Support
    dreadedEvent: 'er',         // \u00c9v\u00e9nement Redout\u00e9
    securityControl: 'mc',      // Mesure de Contr\u00f4le

    // Workshop 2 entities
    sourceRisque: 'sr',         // Source de Risque
    objectifVise: 'ov',         // Objectif Vis\u00e9
    coupleSROV: 'cp',           // Couple SR/OV
    threatCategory: 'tc',       // Cat\u00e9gorie de Menace

    // Workshop 3 entities
    stakeholder: 'pp',          // Partie Prenante
    strategicScenario: 'ss',    // Sc\u00e9nario Strat\u00e9gique
    attackPath: 'ca',           // Chemin d'Attaque
    ecosystemMeasure: 'me',     // Mesure \u00c9cosyst\u00e8me

    // Workshop 4 entities
    operationalScenario: 'so',  // Sc\u00e9nario Op\u00e9rationnel
    ctiResult: 'cti',           // R\u00e9sultat CTI
    elementaryAction: 'ea',     // Action \u00c9l\u00e9mentaire

    // Workshop 5 entities
    riskTreatment: 'tr',        // Traitement du Risque
    residualRisk: 'rr',         // Risque R\u00e9siduel

    // Mapping entities (use combined format)
    mapping: 'map'              // Generic mapping
};

/**
 * Display names for entity types (French)
 */
export const ENTITY_DISPLAY_NAMES_FR = {
    participant: 'Participant',
    responsiblePerson: 'Responsable',
    businessValue: 'Valeur M\u00e9tier',
    supportAsset: 'Bien Support',
    dreadedEvent: '\u00c9v\u00e9nement Redout\u00e9',
    securityControl: 'Mesure de Contr\u00f4le',
    sourceRisque: 'Source de Risque',
    objectifVise: 'Objectif Vis\u00e9',
    coupleSROV: 'Couple SR/OV',
    threatCategory: 'Cat\u00e9gorie de Menace',
    stakeholder: 'Partie Prenante',
    strategicScenario: 'Sc\u00e9nario Strat\u00e9gique',
    attackPath: 'Chemin d\'Attaque',
    ecosystemMeasure: 'Mesure \u00c9cosyst\u00e8me',
    operationalScenario: 'Sc\u00e9nario Op\u00e9rationnel',
    ctiResult: 'R\u00e9sultat CTI',
    elementaryAction: 'Action \u00c9l\u00e9mentaire',
    riskTreatment: 'Traitement du Risque',
    residualRisk: 'Risque R\u00e9siduel',
    mapping: 'Mapping'
};

/**
 * Display names for entity types (English)
 */
export const ENTITY_DISPLAY_NAMES_EN = {
    participant: 'Participant',
    responsiblePerson: 'Responsible Person',
    businessValue: 'Business Value',
    supportAsset: 'Support Asset',
    dreadedEvent: 'Feared Event',
    securityControl: 'Security Control',
    sourceRisque: 'Risk Source',
    objectifVise: 'Target Objective',
    coupleSROV: 'RS/TO Couple',
    threatCategory: 'Threat Category',
    stakeholder: 'Stakeholder',
    strategicScenario: 'Strategic Scenario',
    attackPath: 'Attack Path',
    ecosystemMeasure: 'Ecosystem Measure',
    operationalScenario: 'Operational Scenario',
    ctiResult: 'CTI Result',
    elementaryAction: 'Elementary Action',
    riskTreatment: 'Risk Treatment',
    residualRisk: 'Residual Risk',
    mapping: 'Mapping'
};

/**
 * Generate a short analysis ID from a MongoDB ObjectId
 * @param {string} mongoId - Full MongoDB ObjectId (24 hex characters)
 * @returns {string} - Short ID (e.g., "A7F3D")
 */
export function generateAnalysisShortId(mongoId) {
    if (!mongoId || typeof mongoId !== 'string') {
        // Fallback for invalid/missing ID
        return `A${Date.now().toString(36).toUpperCase().slice(-4)}`;
    }

    // Use last 4 characters of the ObjectId (most unique part)
    const shortPart = mongoId.slice(-4).toUpperCase();
    return `A${shortPart}`;
}

/**
 * Generate a new entity ID
 * @param {string} analysisShortId - Short analysis identifier (e.g., "A7F3D")
 * @param {string} entityType - Type of entity (e.g., 'businessValue')
 * @param {number} sequence - Sequential number (1-based)
 * @returns {string} - Formatted ID (e.g., "A7F3D-vm01")
 */
export function generateEntityId(analysisShortId, entityType, sequence) {
    const prefix = ENTITY_PREFIXES[entityType];
    if (!prefix) {
        console.warn(`Unknown entity type: ${entityType}. Using 'unknown' prefix.`);
        return `${analysisShortId}-unk${String(sequence).padStart(2, '0')}`;
    }

    const paddedSequence = String(sequence).padStart(2, '0');
    return `${analysisShortId}-${prefix}${paddedSequence}`;
}

/**
 * Generate a composite ID for entities with parent relationships
 * Example: Support asset under a business value -> "A7F3D-vm01-bs01"
 * @param {string} parentId - Parent entity ID (e.g., "A7F3D-vm01")
 * @param {string} childEntityType - Child entity type (e.g., 'supportAsset')
 * @param {number} sequence - Sequential number for this child
 * @returns {string} - Composite ID (e.g., "A7F3D-vm01-bs01")
 */
export function generateChildEntityId(parentId, childEntityType, sequence) {
    const prefix = ENTITY_PREFIXES[childEntityType];
    if (!prefix) {
        console.warn(`Unknown entity type: ${childEntityType}. Using 'unk' prefix.`);
        return `${parentId}-unk${String(sequence).padStart(2, '0')}`;
    }

    const paddedSequence = String(sequence).padStart(2, '0');
    return `${parentId}-${prefix}${paddedSequence}`;
}

/**
 * Generate a mapping ID for entity relationships
 * Example: Source Risk to Dreaded Event mapping -> "A7F3D-sr01-er01"
 * @param {string} sourceId - Source entity ID (e.g., "A7F3D-sr01")
 * @param {string} targetId - Target entity ID (e.g., "A7F3D-er01")
 * @returns {string} - Mapping ID
 */
export function generateMappingId(sourceId, targetId) {
    // Convert to strings if needed (for backward compatibility with old numeric IDs)
    const sourceIdStr = String(sourceId);
    const targetIdStr = String(targetId);

    // Extract just the entity part (e.g., "sr01" from "A7F3D-sr01")
    const sourceMatch = sourceIdStr.match(/-([a-z]+\d+)$/);
    const targetMatch = targetIdStr.match(/-([a-z]+\d+)$/);

    if (sourceMatch && targetMatch) {
        // Extract analysis short ID from source
        const analysisShortId = sourceIdStr.split('-')[0];
        return `${analysisShortId}-${sourceMatch[1]}-${targetMatch[1]}`;
    }

    // Fallback: just concatenate (for old IDs that don't match the pattern)
    return `${sourceIdStr}-${targetIdStr.split('-').pop()}`;
}

/**
 * Parse an entity ID to extract its components
 * @param {string} entityId - Entity ID (e.g., "A7F3D-vm01" or "A7F3D-vm01-bs01")
 * @returns {Object|null} - Parsed components or null if invalid
 */
export function parseEntityId(entityId) {
    if (!entityId || typeof entityId !== 'string') {
        return null;
    }

    // Simple entity pattern: A7F3D-vm01
    const simpleMatch = entityId.match(/^(A[A-Z0-9]+)-([a-z]+)(\d+)$/);
    if (simpleMatch) {
        const [, analysisShortId, prefix, sequence] = simpleMatch;
        const entityType = Object.entries(ENTITY_PREFIXES)
            .find(([, p]) => p === prefix)?.[0] || 'unknown';

        return {
            analysisShortId,
            entityType,
            prefix,
            sequence: parseInt(sequence, 10),
            isChild: false,
            raw: entityId
        };
    }

    // Child entity pattern: A7F3D-vm01-bs01
    const childMatch = entityId.match(/^(A[A-Z0-9]+)-([a-z]+)(\d+)-([a-z]+)(\d+)$/);
    if (childMatch) {
        const [, analysisShortId, parentPrefix, parentSeq, childPrefix, childSeq] = childMatch;
        const parentType = Object.entries(ENTITY_PREFIXES)
            .find(([, p]) => p === parentPrefix)?.[0] || 'unknown';
        const childType = Object.entries(ENTITY_PREFIXES)
            .find(([, p]) => p === childPrefix)?.[0] || 'unknown';

        return {
            analysisShortId,
            entityType: childType,
            prefix: childPrefix,
            sequence: parseInt(childSeq, 10),
            parentType,
            parentPrefix,
            parentSequence: parseInt(parentSeq, 10),
            parentId: `${analysisShortId}-${parentPrefix}${parentSeq}`,
            isChild: true,
            raw: entityId
        };
    }

    // Mapping pattern: A7F3D-sr01-er01
    const mappingMatch = entityId.match(/^(A[A-Z0-9]+)-([a-z]+)(\d+)-([a-z]+)(\d+)$/);
    if (mappingMatch) {
        // This is the same as child pattern, but we interpret differently based on context
        return parseEntityId(entityId); // Returns child interpretation
    }

    return null;
}

/**
 * Check if an ID belongs to a specific analysis
 * @param {string} entityId - Entity ID to check
 * @param {string} analysisShortId - Analysis short ID
 * @returns {boolean}
 */
export function belongsToAnalysis(entityId, analysisShortId) {
    if (!entityId || !analysisShortId) return false;
    return entityId.startsWith(`${analysisShortId}-`);
}

/**
 * Check if an ID is of a specific entity type
 * @param {string} entityId - Entity ID to check
 * @param {string} entityType - Entity type to match
 * @returns {boolean}
 */
export function isEntityType(entityId, entityType) {
    const prefix = ENTITY_PREFIXES[entityType];
    if (!prefix || !entityId) return false;

    // Match the prefix either directly after analysis ID or after parent ID
    const regex = new RegExp(`-${prefix}\\d+$`);
    return regex.test(entityId);
}

/**
 * Extract the analysis short ID from any entity ID
 * @param {string} entityId - Entity ID
 * @returns {string|null} - Analysis short ID or null
 */
export function extractAnalysisShortId(entityId) {
    if (!entityId || typeof entityId !== 'string') return null;
    const match = entityId.match(/^(A[A-Z0-9]+)-/);
    return match ? match[1] : null;
}

/**
 * Generate the next ID for an entity type within an analysis
 * @param {string} analysisShortId - Short analysis identifier
 * @param {string} entityType - Type of entity
 * @param {Array} existingEntities - Existing entities (must have 'id' property)
 * @returns {string} - Next available ID
 */
export function generateNextId(analysisShortId, entityType, existingEntities = []) {
    const prefix = ENTITY_PREFIXES[entityType];
    if (!prefix) {
        throw new Error(`Unknown entity type: ${entityType}`);
    }

    const regex = new RegExp(`^${analysisShortId}-${prefix}(\\d+)$`);

    let maxSequence = 0;
    existingEntities.forEach(entity => {
        if (!entity.id) return;
        // Convert ID to string to handle both old numeric IDs and new string IDs
        const entityIdStr = String(entity.id);
        const match = entityIdStr.match(regex);
        if (match) {
            maxSequence = Math.max(maxSequence, parseInt(match[1], 10));
        }
    });

    return generateEntityId(analysisShortId, entityType, maxSequence + 1);
}

/**
 * Generate the next child ID for an entity type under a parent
 * @param {string} parentId - Parent entity ID (e.g., "A7F3D-vm01")
 * @param {string} childEntityType - Child entity type
 * @param {Array} existingChildren - Existing child entities (must have 'id' property)
 * @returns {string} - Next available child ID
 */
export function generateNextChildId(parentId, childEntityType, existingChildren = []) {
    const prefix = ENTITY_PREFIXES[childEntityType];
    if (!prefix) {
        throw new Error(`Unknown entity type: ${childEntityType}`);
    }

    const regex = new RegExp(`^${parentId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-${prefix}(\\d+)$`);

    let maxSequence = 0;
    existingChildren.forEach(child => {
        if (!child.id) return;
        // Convert ID to string to handle both old numeric IDs and new string IDs
        const childIdStr = String(child.id);
        const match = childIdStr.match(regex);
        if (match) {
            maxSequence = Math.max(maxSequence, parseInt(match[1], 10));
        }
    });

    return generateChildEntityId(parentId, childEntityType, maxSequence + 1);
}

/**
 * Get display name for an entity ID (human-readable)
 * @param {string} entityId - Entity ID
 * @param {string} language - 'fr' or 'en'
 * @returns {string} - Human-readable name (e.g., "Business Value 01")
 */
export function getDisplayName(entityId, language = 'fr') {
    const parsed = parseEntityId(entityId);
    if (!parsed) return entityId;

    const names = language === 'en' ? ENTITY_DISPLAY_NAMES_EN : ENTITY_DISPLAY_NAMES_FR;
    const entityName = names[parsed.entityType] || parsed.entityType;

    if (parsed.isChild) {
        const parentName = names[parsed.parentType] || parsed.parentType;
        return `${parentName} ${parsed.parentSequence} - ${entityName} ${parsed.sequence}`;
    }

    return `${entityName} ${parsed.sequence}`;
}

/**
 * Validate an entity ID format
 * @param {string} entityId - Entity ID to validate
 * @returns {Object} - { valid: boolean, error?: string }
 */
export function validateEntityId(entityId) {
    if (!entityId || typeof entityId !== 'string') {
        return { valid: false, error: 'ID must be a non-empty string' };
    }

    // Check basic format
    if (!entityId.match(/^A[A-Z0-9]+(-[a-z]+\d+)+$/)) {
        return { valid: false, error: 'Invalid ID format. Expected: A{shortId}-{prefix}{sequence}' };
    }

    // Extract and validate prefix
    const parsed = parseEntityId(entityId);
    if (!parsed) {
        return { valid: false, error: 'Could not parse entity ID' };
    }

    if (parsed.entityType === 'unknown') {
        return { valid: false, error: `Unknown entity prefix: ${parsed.prefix}` };
    }

    return { valid: true };
}

/**
 * @typedef {Object} IdGeneratorConfig
 * @property {string} analysisId - Full MongoDB ObjectId of the analysis
 * @property {string} [analysisShortId] - Pre-computed short ID (optional)
 */

/**
 * Create a bound ID generator for a specific analysis
 * Useful for components that need to generate many IDs
 * @param {IdGeneratorConfig} config - Configuration
 * @returns {Object} - Bound ID generation functions
 */
export function createIdGenerator(config) {
    const { analysisId, analysisShortId: providedShortId } = config;
    const shortId = providedShortId || generateAnalysisShortId(analysisId);

    return {
        analysisShortId: shortId,

        generate(entityType, sequence) {
            return generateEntityId(shortId, entityType, sequence);
        },

        generateChild(parentId, childEntityType, sequence) {
            return generateChildEntityId(parentId, childEntityType, sequence);
        },

        generateNext(entityType, existingEntities) {
            return generateNextId(shortId, entityType, existingEntities);
        },

        generateNextChild(parentId, childEntityType, existingChildren) {
            return generateNextChildId(parentId, childEntityType, existingChildren);
        },

        generateMapping(sourceId, targetId) {
            return generateMappingId(sourceId, targetId);
        },

        belongs(entityId) {
            return belongsToAnalysis(entityId, shortId);
        },

        isType(entityId, entityType) {
            return isEntityType(entityId, entityType);
        },

        parse(entityId) {
            return parseEntityId(entityId);
        },

        displayName(entityId, language) {
            return getDisplayName(entityId, language);
        }
    };
}

// Default export for convenience
export default {
    ENTITY_PREFIXES,
    ENTITY_DISPLAY_NAMES_FR,
    ENTITY_DISPLAY_NAMES_EN,
    generateAnalysisShortId,
    generateEntityId,
    generateChildEntityId,
    generateMappingId,
    parseEntityId,
    belongsToAnalysis,
    isEntityType,
    extractAnalysisShortId,
    generateNextId,
    generateNextChildId,
    getDisplayName,
    validateEntityId,
    createIdGenerator
};
